import _ from 'lodash';
import { ScreenSizeClass } from '../_utils';
export var TABLE_THEME;
(function (TABLE_THEME) {
    TABLE_THEME["ROW"] = "ROW";
    TABLE_THEME["OUTLINE"] = "OUTLINE";
    TABLE_THEME["ZEBRA"] = "ZEBRA";
})(TABLE_THEME || (TABLE_THEME = {}));
export var TABLE_CONTENT_DENSITY;
(function (TABLE_CONTENT_DENSITY) {
    TABLE_CONTENT_DENSITY["REGULAR"] = "REGULAR";
    TABLE_CONTENT_DENSITY["COMPACT"] = "COMPACT";
})(TABLE_CONTENT_DENSITY || (TABLE_CONTENT_DENSITY = {}));
export const THEME_TO_CLASSNAMES = {
    [TABLE_THEME.ROW]: 'bk-row',
    [TABLE_THEME.OUTLINE]: 'bk-outline',
    [TABLE_THEME.ZEBRA]: 'bk-zebra',
};
export var sortType;
(function (sortType) {
    sortType["ASC"] = "asc";
    sortType["DESC"] = "desc";
})(sortType || (sortType = {}));
export var TableThemeMode;
(function (TableThemeMode) {
    TableThemeMode["LIGHT"] = "LIGHT";
    TableThemeMode["DARK"] = "DARK";
})(TableThemeMode || (TableThemeMode = {}));
export var LINK_TARGET;
(function (LINK_TARGET) {
    LINK_TARGET["SELF"] = "_self";
    LINK_TARGET["BLANK"] = "_target";
})(LINK_TARGET || (LINK_TARGET = {}));
export const DEFAULT_ROW_HEIGHT = 32;
export const ROW_CONTAINER_SELECTOR = '.ag-center-cols-container';
const MIN_HEADER_HEIGHT = 48;
const RESIZE_HEADER_PADDING = 12;
export const REGULAR_ROW_PADDING = 16;
export const COMPACT_ROW_PADDING = 8;
const HEADER_SELECTORS = '.ag-header-cell [role=button]';
const getHighestHeightOfHeaderChildren = (gridDOMRef) => {
    var _a;
    const headerHeights = [];
    const $headerSelector = (_a = gridDOMRef === null || gridDOMRef === void 0 ? void 0 : gridDOMRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(HEADER_SELECTORS);
    $headerSelector === null || $headerSelector === void 0 ? void 0 : $headerSelector.forEach((header) => {
        var _a, _b, _c;
        // We pass a custom header component to ag grid and ag grid only has a class
        // to select the header - ag-header-cell - so what we do here is select that class
        // and the reason we have children[0] links is to select the text rendered inside our
        // custom header cell component so that wrapping happens accurately
        const height = (_c = (_b = (_a = header.children) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect()) === null || _c === void 0 ? void 0 : _c.height;
        headerHeights.push(height);
    });
    return Math.max(...headerHeights);
};
const calculateHeaderHeight = (maxHeaderHeight) => !_.isNil(maxHeaderHeight)
    ? Math.max(maxHeaderHeight + RESIZE_HEADER_PADDING, MIN_HEADER_HEIGHT)
    : MIN_HEADER_HEIGHT;
export const calculateRowHeight = (heights, density) => Math.max(...heights) +
    (density === TABLE_CONTENT_DENSITY.COMPACT
        ? COMPACT_ROW_PADDING
        : REGULAR_ROW_PADDING);
/*
    Below method to calculate the row-height dynamically based on the content of the cell.
    We are binding custom html to the ag-grid cell via <SafeHTML>
    We have avoided the use of 'autoHeight' to improve the performance,
    but to mimic the behaviour of the same we need to calculate the row-height while re-sizing
*/
export const getHighestHeightOfRowChildren = (params, $tableSelector, density, 
/** callback with update height */
updateCb) => {
    const cellHeights = [];
    const row = $tableSelector[0].querySelector(`[row-index="${params.node.rowIndex}"]`);
    if (row) {
        row.childNodes.forEach((cell) => {
            const firstChild = cell.firstChild;
            if (firstChild instanceof Element) {
                const cellHeight = parseInt(getComputedStyle(firstChild).height, 10);
                const borderWidth = parseInt(getComputedStyle(cell).borderBottomWidth, 10);
                cellHeights.push(cellHeight + borderWidth * 2);
                try {
                    const img = firstChild.querySelector('img');
                    if (img) {
                        if (!img.complete) {
                            // set row height to 3x of the default
                            cellHeights.push(DEFAULT_ROW_HEIGHT * 3);
                            // in the onLoad callback set the row height and ask ag to re-render
                            img.onload = () => {
                                cellHeights.push(firstChild.getBoundingClientRect().height);
                                const height = calculateRowHeight(cellHeights, density);
                                updateCb(height);
                                params.node.setRowHeight(height);
                                params.api.onRowHeightChanged();
                            };
                        }
                    }
                }
                catch (_a) {
                    //
                }
            }
        });
    }
    if (!cellHeights.length) {
        return undefined;
    }
    const height = calculateRowHeight(cellHeights, density);
    updateCb(height);
    return height;
};
const setHeaderHeight = (gridAPI, height) => {
    gridAPI.current.setHeaderHeight(height);
};
// resetHeaderHeightTimer will help prevent unnecessary re-calculation of the header height
let resetHeaderHeightTimer;
export const resetHeaderHeight = (gridApi, gridDOMRef) => {
    if (resetHeaderHeightTimer) {
        clearTimeout(resetHeaderHeightTimer);
    }
    if (!gridDOMRef.current || !gridApi.current) {
        return;
    }
    resetHeaderHeightTimer = setTimeout(() => {
        const maxHeaderHeight = getHighestHeightOfHeaderChildren(gridDOMRef);
        setHeaderHeight(gridApi, calculateHeaderHeight(maxHeaderHeight));
    });
};
export const getDataFromSelectionRange = (selectedRange, gridApi, columns, context) => {
    var _a, _b;
    if (selectedRange.startRowIndex < 0 || selectedRange.startColumnIndex < 0) {
        return '';
    }
    const rows = [];
    const startRowIndex = Math.min(selectedRange.startRowIndex, selectedRange.endRowIndex);
    const endRowIndex = Math.max(selectedRange.startRowIndex, selectedRange.endRowIndex);
    const startColumnIndex = Math.min(selectedRange.startColumnIndex, selectedRange.endColumnIndex);
    const endColumnIndex = Math.max(selectedRange.startColumnIndex, selectedRange.endColumnIndex);
    for (let i = startRowIndex; i <= endRowIndex; i += 1) {
        const row = [];
        const rowData = (_b = (_a = gridApi.current) === null || _a === void 0 ? void 0 : _a.getRowNode(`${i}`)) === null || _b === void 0 ? void 0 : _b.data;
        for (let j = startColumnIndex; j <= endColumnIndex; j += 1) {
            const column = columns[j];
            let cellData = rowData[column.id];
            if (column.valueFormatter) {
                cellData = column.valueFormatter(rowData[column.id], {
                    context,
                    rowData,
                });
            }
            const formattedValue = _.unescape(cellData);
            row.push(`${formattedValue}`.replace(/<[^>]*>/g, ''));
        }
        rows.push(row.join('\t'));
    }
    return rows.join('\r\n');
};
export const disableClickGridCells = ['hoverAction', 'menu'];
/**
 * Scaling factors for each screen (as in _scaling.scss):
 * HD = 1
 * FHD = 1.4
 * QHD = 1.7
 * UHD = 2.5
 *
 * We use root font size of 14px, i.e., 1rem = 14px
 * while modern browsers have root font size set to 16px.
 * Therefore, we need to multiply above factors with (16/14).
 * Revised scaling factors for each screen:
 * HD = 1.14
 * FHD = 1.6
 * QHD = 1.94
 * UHD = 2.86
 *
 * Calculated summary heights =
 * revised scaling factor * normal table summary height (64)
 * rounding off to nearest multiple of eight
 */
export const screenSizeToSummaryHeightMapping = {
    [ScreenSizeClass.HD]: 80,
    [ScreenSizeClass.FHD]: 104,
    [ScreenSizeClass.QHD]: 128,
    [ScreenSizeClass.UHD]: 184,
};
export const getColDefsWithSortLabel = (column, sort, sortAscending, sortKeyMap) => {
    return column.map((col) => {
        if (sortKeyMap[col.colId] === sort) {
            return {
                ...col,
                sort: sortAscending ? sortType.ASC : sortType.DESC,
            };
        }
        const colWithoutSortLabel = col;
        delete colWithoutSortLabel.sort;
        return colWithoutSortLabel;
    });
};
export const encodeURIForCaption = (link) => {
    try {
        // Decoding the URI component first to handle any existing encoding
        // Then, encoding it to ensure proper URI formatting and encoding for special characters
        return encodeURI(decodeURI(link));
    }
    catch (error) {
        // Handle invalid encoding by returning the original URL
        return link;
    }
};
/**
 * Calculates margin based on leading spaces in the provided text.
 *
 * @param {string} text - The text string to analyze for leading spaces.
 * @returns {Object} - Object containing leading and trailing spaces (in pixels).
 */
export const calculateMarginBySpaces = (text) => {
    var _a, _b;
    // Ensure the input is a string, otherwise return 0px margin
    if (typeof text !== 'string')
        return { leading: 0, trailing: 0 };
    // Check if the string starts with an HTML tag (e.g., <font size="3">)
    const tagMatch = text.match(/^<[^>]+>/);
    let newText = text;
    if (tagMatch) {
        // Remove the tag from the string to calculate leading spaces correctly
        newText = text.slice(tagMatch[0].length);
    }
    // Count leading spaces
    const leadingSpaces = ((_a = newText.match(/^\s+/)) === null || _a === void 0 ? void 0 : _a[0].length) || 0;
    // Count trailing spaces
    const trailingSpaces = ((_b = newText.match(/\s+$/)) === null || _b === void 0 ? void 0 : _b[0].length) || 0;
    // Convert the space count to pixels (assuming 3px per space, can be adjusted)
    const spaceToPixelRatio = 3;
    return {
        leading: leadingSpaces * spaceToPixelRatio,
        trailing: trailingSpaces * spaceToPixelRatio,
    };
};
//# sourceMappingURL=table-utils.js.map